import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { applicationSubmitSchema, applicationUpdateSchema, ENROLMENT_LIMITS, parseSymbolList } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import { fmtDate } from '../lib/format';
import { Button, Card, cx, ErrorText, Field, Input, Pill, Select, Textarea } from '../components/ui';

/** INT-ENR-01: public application form (also opened from an invite link) and the applicant's private status page. */

interface PolicyDoc {
  id: string;
  kind: string;
  label: string;
  version: number;
  title: string;
  body: string;
  publishedAt: string;
}
interface FormOptions {
  markets: { code: string; name: string }[];
  policies: PolicyDoc[];
}
interface InvitePrefill {
  email: string | null;
  name: string | null;
  marketsOfInterest: string[];
  invitedBy: string;
  expiresAt: string;
}
interface StatusView {
  name: string;
  email: string;
  status: 'SUBMITTED' | 'UNDER_REVIEW' | 'MORE_INFO' | 'APPROVED' | 'REJECTED';
  statusLabel: string;
  submittedAt: string;
  updatedAt: string;
  invited: boolean;
  message: string | null;
  activated: boolean | null;
  canUpdate: boolean;
  fields: {
    name: string;
    phone: string | null;
    country: string | null;
    timezone: string;
    experienceYears: number | null;
    marketsOfInterest: string[];
    preferredSymbols: string[];
    brokers: string | null;
    statement: string | null;
    referral: string | null;
  } | null;
}
type Errors = Record<string, string>;

const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/** Centered page for people who aren't signed in (matches the sign-in screen). */
export function PublicFrame({ children, wide }: { children: ReactNode; wide?: boolean }) {
  return (
    <div className="flex min-h-screen justify-center px-4 py-10">
      <div className={cx('flex w-full flex-col gap-4', wide ? 'max-w-[780px]' : 'max-w-[480px]')}>
        <div className="flex items-center gap-3 px-1">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#2b4a78] bg-accent-bg font-mono text-sm font-semibold text-accent">VTF</div>
          <div className="flex flex-col">
            <span className="text-lg font-bold">Virtual Trading Floor</span>
            <span className="text-sm text-sub">Private desk · members only</span>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

export function TimezoneSelect({ value, onChange, ...rest }: { value: string; onChange: (v: string) => void; 'aria-invalid'?: boolean }) {
  const zones = useMemo(() => {
    const all = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
    return [...new Set([...all, 'UTC', value].filter(Boolean))].sort();
  }, [value]);
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} {...rest}>
      {zones.map((z) => (
        <option key={z} value={z}>
          {z.replace(/_/g, ' ')}
        </option>
      ))}
    </Select>
  );
}

/** Policy text as written by the Admin (line breaks kept). */
export function PolicyText({ body, className }: { body: string; className?: string }) {
  return <div className={cx('whitespace-pre-wrap text-sm leading-relaxed text-ink2', className)}>{body}</div>;
}

function issuesOf(error: { issues: { path: PropertyKey[]; message: string }[] }): Errors {
  const out: Errors = {};
  for (const i of error.issues) {
    const k = String(i.path[0] ?? 'form');
    out[k] ??= i.message;
  }
  return out;
}

/** Field with an inline error under it (linked to the control by Field's aria-describedby). */
function F({ label, error, hint, className, children }: { label: string; error?: string; hint?: ReactNode; className?: string; children: ReactNode }) {
  return (
    <Field label={label} className={className} hint={error ? <span className="text-down">{error}</span> : hint}>
      {children}
    </Field>
  );
}

function MarketChips({ markets, value, onChange, labelId }: { markets: { code: string; name: string }[]; value: string[]; onChange: (v: string[]) => void; labelId: string }) {
  return (
    <div role="group" aria-labelledby={labelId} className="flex flex-wrap gap-1.5">
      {markets.map((m) => {
        const on = value.includes(m.code);
        return (
          <button
            key={m.code}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? value.filter((x) => x !== m.code) : [...value, m.code])}
            className={cx('min-h-10 cursor-pointer rounded-full px-3.5 py-1.5 text-sm', on ? 'bg-accent font-semibold text-bg' : 'border border-line2 bg-raised text-sub')}
          >
            {m.name} <span className={on ? 'text-bg/70' : 'text-muted'}>· {m.code}</span>
          </button>
        );
      })}
    </div>
  );
}

const emptyForm = () => ({
  name: '',
  email: '',
  phone: '',
  country: '',
  timezone: browserZone(),
  experienceYears: '',
  marketsOfInterest: [] as string[],
  symbols: '',
  brokers: '',
  statement: '',
  referral: '',
  website: '',
});

export function Apply() {
  const { token: inviteToken } = useParams();
  const options = useQuery({ queryKey: ['apply-form'], queryFn: () => api<FormOptions>('/apply/form'), staleTime: 300_000 });
  const invite = useQuery({ queryKey: ['invite', inviteToken], queryFn: () => api<InvitePrefill>(`/apply/invite/${inviteToken}`), enabled: !!inviteToken, retry: false });
  const [f, setF] = useState(emptyForm);
  const [accepted, setAccepted] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const set = (patch: Partial<ReturnType<typeof emptyForm>>) => setF((x) => ({ ...x, ...patch }));

  useEffect(() => {
    const p = invite.data;
    if (p) setF((x) => ({ ...x, email: p.email ?? x.email, name: p.name ?? x.name, marketsOfInterest: p.marketsOfInterest.length ? p.marketsOfInterest : x.marketsOfInterest }));
  }, [invite.data]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFailure(null);
    const body = {
      name: f.name,
      email: f.email,
      phone: f.phone,
      country: f.country,
      timezone: f.timezone,
      experienceYears: f.experienceYears === '' ? undefined : Number(f.experienceYears),
      marketsOfInterest: f.marketsOfInterest,
      preferredSymbols: parseSymbolList(f.symbols),
      brokers: f.brokers,
      statement: f.statement,
      referral: f.referral,
      acceptTerms: accepted,
      acceptedPolicyIds: options.data?.policies.map((p) => p.id) ?? [],
      ...(invite.data && inviteToken ? { inviteToken } : {}),
      ...(f.website ? { website: f.website } : {}),
    };
    const parsed = applicationSubmitSchema.safeParse(body);
    if (!parsed.success) {
      setErrors(issuesOf(parsed.error));
      setFailure('Some answers need attention — see the highlighted fields.');
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      const r = await api<{ message: string }>('/applications', { body });
      setDone(r.message);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setFailure(errorText(err));
      if (String(errorText(err)).includes('updated')) void options.refetch();
    } finally {
      setBusy(false);
    }
  }

  if (done)
    return (
      <PublicFrame>
        <Card tone="up" className="flex flex-col gap-4 p-6">
          <h1 className="m-0 text-xl font-bold">Application sent</h1>
          <p className="m-0 text-sub">{done}</p>
          <ol className="m-0 flex list-decimal flex-col gap-2 pl-5 text-sm text-ink2">
            <li>Open the email from us and keep the private link — it shows where your application is.</li>
            <li>An admin reviews your application. If they need more from you, you’ll get an email.</li>
            <li>If you’re approved, you’ll get an activation link to set your password. It works for 72 hours.</li>
          </ol>
          <Link to="/login" className="text-sm">
            Back to sign in
          </Link>
        </Card>
      </PublicFrame>
    );

  const len = (v: string, max: number) => <span className={v.length > max ? 'text-down' : ''}>{v.length.toLocaleString()} / {max.toLocaleString()}</span>;
  return (
    <PublicFrame wide>
      <Card className="flex flex-col gap-1 p-6">
        <h1 className="m-0 text-2xl font-bold tracking-tight">Apply to join the floor</h1>
        <p className="m-0 text-sub">VTF is a private trading floor. Tell us about yourself and your trading, and an admin will review your application.</p>
        {inviteToken && invite.data && (
          <p className="m-0 mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-[#22385a] bg-accent-bg px-3 py-2 text-sm">
            <Pill tone="accent">Invited</Pill> {invite.data.invitedBy} invited you — your application goes to the front of the review queue.
          </p>
        )}
        {inviteToken && invite.error && <p className="m-0 mt-3 rounded-lg border border-[#5a4316] bg-warn-bg px-3 py-2 text-sm text-warn">{errorText(invite.error)}</p>}
      </Card>

      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Card className="flex flex-col gap-3 p-5">
          <h2 className="m-0 text-[15px] font-semibold">About you</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <F label="Full name" error={errors.name}>
              <Input autoComplete="name" maxLength={ENROLMENT_LIMITS.name} value={f.name} onChange={(e) => set({ name: e.target.value })} aria-invalid={!!errors.name} />
            </F>
            <F label="Email" error={errors.email} hint={invite.data?.email ? 'This invite is for this address.' : undefined}>
              <Input type="email" autoComplete="email" maxLength={ENROLMENT_LIMITS.email} value={f.email} readOnly={!!invite.data?.email} onChange={(e) => set({ email: e.target.value })} aria-invalid={!!errors.email} />
            </F>
            <F label="Mobile number" error={errors.phone} hint="With country code, e.g. +92 300 1234567">
              <Input type="tel" autoComplete="tel" maxLength={ENROLMENT_LIMITS.phone} value={f.phone} onChange={(e) => set({ phone: e.target.value })} aria-invalid={!!errors.phone} />
            </F>
            <F label="Country" error={errors.country}>
              <Input autoComplete="country-name" maxLength={ENROLMENT_LIMITS.country} value={f.country} onChange={(e) => set({ country: e.target.value })} aria-invalid={!!errors.country} />
            </F>
            <F label="Your timezone" error={errors.timezone} hint="Deadlines and times are shown in this zone." className="sm:col-span-2">
              <TimezoneSelect value={f.timezone} onChange={(timezone) => set({ timezone })} aria-invalid={!!errors.timezone} />
            </F>
          </div>
        </Card>

        <Card className="flex flex-col gap-3 p-5">
          <h2 className="m-0 text-[15px] font-semibold">Your trading</h2>
          <div className="grid gap-3 sm:grid-cols-[180px_1fr]">
            <F label="Years of experience" error={errors.experienceYears}>
              <Input type="number" inputMode="numeric" min={0} max={60} value={f.experienceYears} onChange={(e) => set({ experienceYears: e.target.value })} aria-invalid={!!errors.experienceYears} />
            </F>
            <div className="flex flex-col gap-1.5">
              <span id="markets-label" className="text-xs text-muted">
                Markets you’re interested in
              </span>
              {options.data ? <MarketChips labelId="markets-label" markets={options.data.markets} value={f.marketsOfInterest} onChange={(marketsOfInterest) => set({ marketsOfInterest })} /> : <span className="text-sm text-muted">Loading markets…</span>}
              {errors.marketsOfInterest && <span className="text-xs text-down">{errors.marketsOfInterest}</span>}
            </div>
          </div>
          <F label="Symbols you’d like to cover" error={errors.preferredSymbols} hint="Separate with commas, e.g. OGDC, LUCK, EURUSD">
            <Input value={f.symbols} maxLength={ENROLMENT_LIMITS.symbols * (ENROLMENT_LIMITS.symbol + 2)} onChange={(e) => set({ symbols: e.target.value })} aria-invalid={!!errors.preferredSymbols} />
          </F>
          <F label="Brokers you use (optional)" error={errors.brokers}>
            <Input maxLength={ENROLMENT_LIMITS.brokers} value={f.brokers} onChange={(e) => set({ brokers: e.target.value })} />
          </F>
          <F label="About your trading" error={errors.statement} hint={<>How you trade, what you want from the floor. {len(f.statement, ENROLMENT_LIMITS.statement)}</>}>
            <Textarea rows={5} maxLength={ENROLMENT_LIMITS.statement} value={f.statement} onChange={(e) => set({ statement: e.target.value })} aria-invalid={!!errors.statement} />
          </F>
          <F label="Who referred you? (optional)" error={errors.referral}>
            <Input maxLength={ENROLMENT_LIMITS.referral} value={f.referral} onChange={(e) => set({ referral: e.target.value })} />
          </F>
          {/* Honeypot: hidden from people and screen readers; automated sign-ups fill it in. */}
          <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
            <label>
              Website
              <input tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set({ website: e.target.value })} />
            </label>
          </div>
        </Card>

        <Card className="flex flex-col gap-3 p-5">
          <h2 className="m-0 text-[15px] font-semibold">Floor documents</h2>
          <p className="m-0 text-sm text-sub">Please read these before you apply. Members accept them again whenever they change.</p>
          {options.isLoading && <p className="m-0 text-sm text-muted">Loading…</p>}
          {options.data?.policies.map((p) => (
            <details key={p.id} className="group rounded-lg border border-line2 bg-raised">
              <summary className="flex cursor-pointer items-center justify-between gap-2 px-3.5 py-2.5 font-semibold">
                <span>{p.title}</span>
                <span className="text-xs font-normal text-muted">version {p.version} · read</span>
              </summary>
              <PolicyText body={p.body} className="max-h-[40vh] overflow-y-auto border-t border-line px-3.5 py-3" />
            </details>
          ))}
          <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-line2 px-3.5 py-3">
            <input type="checkbox" className="mt-0.5 h-[18px] w-[18px] shrink-0" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} aria-invalid={!!errors.acceptTerms} />
            <span>I have read and accept the floor rules, the confidentiality terms and the general-advice disclaimer.</span>
          </label>
          {errors.acceptTerms && <span className="text-xs text-down">{errors.acceptTerms}</span>}
        </Card>

        <ErrorText error={failure} />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm text-sub">
            Already a member? <Link to="/login">Sign in</Link>
          </span>
          <Button type="submit" variant="primary" disabled={busy || !options.data}>
            {busy ? 'Sending…' : 'Submit application'}
          </Button>
        </div>
      </form>
    </PublicFrame>
  );
}

// ───────────────────────── Status page ─────────────────────────

const STEPS = [
  { key: 'SUBMITTED', label: 'Submitted' },
  { key: 'UNDER_REVIEW', label: 'Under review' },
  { key: 'DECISION', label: 'Decision' },
];

function Steps({ status, label }: { status: StatusView['status']; label: string }) {
  const at = status === 'SUBMITTED' ? 0 : status === 'UNDER_REVIEW' || status === 'MORE_INFO' ? 1 : 2;
  return (
    <ol aria-label="Application progress" className="m-0 grid list-none grid-cols-3 gap-2 p-0">
      {STEPS.map((s, i) => {
        const state = i < at ? 'done' : i === at ? 'now' : 'next';
        const text = i === 2 && at === 2 ? label : i === 1 && status === 'MORE_INFO' ? 'More info needed' : s.label;
        return (
          <li key={s.key} aria-current={state === 'now' ? 'step' : undefined} className="flex flex-col gap-1.5">
            <span className={cx('h-1.5 rounded', state === 'next' ? 'bg-[#232a34]' : status === 'REJECTED' && i === 2 ? 'bg-down' : status === 'MORE_INFO' && i === 1 ? 'bg-warn' : 'bg-accent')} />
            <span className={cx('text-sm', state === 'now' ? 'font-semibold text-ink' : state === 'done' ? 'text-sub' : 'text-muted')}>{text}</span>
          </li>
        );
      })}
    </ol>
  );
}

function LostLink() {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  async function send(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      setMsg((await api<{ message: string }>('/apply/status-link', { body: { email, ...(website ? { website } : {}) } })).message);
    } catch (x) {
      setErr(errorText(x));
    }
  }
  return (
    <Card className="flex flex-col gap-3 p-5">
      <h2 className="m-0 text-[15px] font-semibold">Lost your link?</h2>
      {msg ? (
        <p className="m-0 text-sm text-up">{msg}</p>
      ) : (
        <form onSubmit={send} className="flex flex-col gap-3">
          <p className="m-0 text-sm text-sub">Enter the email you applied with and we’ll send a fresh private link.</p>
          <Field label="Email">
            <Input type="email" autoComplete="email" required maxLength={ENROLMENT_LIMITS.email} value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
            <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
          </div>
          <ErrorText error={err} />
          <Button type="submit" className="self-start">
            Send me a new link
          </Button>
        </form>
      )}
    </Card>
  );
}

function MoreInfoForm({ token, view, onSaved }: { token: string; view: StatusView; onSaved: (v: StatusView) => void }) {
  const fields = view.fields!;
  const [reply, setReply] = useState('');
  const [statement, setStatement] = useState(fields.statement ?? '');
  const [brokers, setBrokers] = useState(fields.brokers ?? '');
  const [symbols, setSymbols] = useState(fields.preferredSymbols.join(', '));
  const [phone, setPhone] = useState(fields.phone ?? '');
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function save(e: FormEvent) {
    e.preventDefault();
    const body = { reply, statement, brokers, phone, preferredSymbols: parseSymbolList(symbols) };
    const parsed = applicationUpdateSchema.safeParse(body);
    if (!parsed.success) {
      setErrors(issuesOf(parsed.error));
      return;
    }
    setErrors({});
    setBusy(true);
    setFailure(null);
    try {
      onSaved(await api<StatusView>(`/apply/status/${token}`, { body }));
    } catch (x) {
      setFailure(errorText(x));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={save} className="flex flex-col gap-3">
      <F label="Your answer" error={errors.reply} hint="The admin sees this next to your application.">
        <Textarea rows={4} maxLength={ENROLMENT_LIMITS.reply} value={reply} onChange={(e) => setReply(e.target.value)} aria-invalid={!!errors.reply} />
      </F>
      <details className="rounded-lg border border-line2 bg-raised">
        <summary className="cursor-pointer px-3.5 py-2.5 text-sm font-semibold">Update your details (optional)</summary>
        <div className="flex flex-col gap-3 border-t border-line p-3.5">
          <F label="About your trading" error={errors.statement}>
            <Textarea rows={4} maxLength={ENROLMENT_LIMITS.statement} value={statement} onChange={(e) => setStatement(e.target.value)} />
          </F>
          <div className="grid gap-3 sm:grid-cols-2">
            <F label="Symbols you’d like to cover" error={errors.preferredSymbols}>
              <Input value={symbols} onChange={(e) => setSymbols(e.target.value)} />
            </F>
            <F label="Brokers you use" error={errors.brokers}>
              <Input maxLength={ENROLMENT_LIMITS.brokers} value={brokers} onChange={(e) => setBrokers(e.target.value)} />
            </F>
            <F label="Mobile number" error={errors.phone}>
              <Input type="tel" maxLength={ENROLMENT_LIMITS.phone} value={phone} onChange={(e) => setPhone(e.target.value)} />
            </F>
          </div>
        </div>
      </details>
      <ErrorText error={failure} />
      <Button type="submit" variant="primary" className="self-start" disabled={busy}>
        {busy ? 'Sending…' : 'Send my answer'}
      </Button>
    </form>
  );
}

export function ApplicationStatusPage() {
  const { token } = useParams();
  const q = useQuery({ queryKey: ['application-status', token], queryFn: () => api<StatusView>(`/apply/status/${token}`), enabled: !!token, retry: false });
  const [saved, setSaved] = useState<StatusView | null>(null);
  const view = saved ?? q.data;
  const tz = browserZone();

  if (!token)
    return (
      <PublicFrame>
        <LostLink />
      </PublicFrame>
    );
  return (
    <PublicFrame>
      {q.isLoading && <Card className="p-6 text-sub">Loading…</Card>}
      {q.error && (
        <>
          <Card tone="warn" className="flex flex-col gap-2 p-6">
            <h1 className="m-0 text-lg font-bold">This link doesn’t work any more</h1>
            <p className="m-0 text-sm text-sub">{errorText(q.error)}</p>
          </Card>
          <LostLink />
        </>
      )}
      {view && (
        <Card className="flex flex-col gap-4 p-6">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="flex flex-col">
              <h1 className="m-0 text-xl font-bold">Your application</h1>
              <span className="text-sm text-sub">
                {view.name} · sent {fmtDate(view.submittedAt, tz)}
              </span>
            </div>
            <div className="flex gap-1.5">
              {view.invited && <Pill tone="accent">Invited</Pill>}
              <Pill tone={view.status === 'APPROVED' ? 'up' : view.status === 'REJECTED' ? 'down' : view.status === 'MORE_INFO' ? 'warn' : 'neutral'}>{view.statusLabel}</Pill>
            </div>
          </div>
          <Steps status={view.status} label={view.statusLabel} />
          {view.status === 'SUBMITTED' && <p className="m-0 text-sm text-ink2">Thanks — your application is in the queue. We’ll email you when an admin has looked at it.</p>}
          {view.status === 'UNDER_REVIEW' && <p className="m-0 text-sm text-ink2">An admin is reviewing your application. We’ll email you as soon as there’s a decision.</p>}
          {view.status === 'MORE_INFO' && (
            <>
              <div className="rounded-lg border border-[#5a4316] bg-warn-bg px-3.5 py-3">
                <p className="m-0 text-xs font-semibold tracking-[0.06em] text-warn uppercase">The admin asks</p>
                <p className="m-0 mt-1 whitespace-pre-wrap text-ink">{view.message}</p>
              </div>
              {token && <MoreInfoForm token={token} view={view} onSaved={setSaved} />}
            </>
          )}
          {view.status === 'APPROVED' &&
            (view.activated ? (
              <p className="m-0 text-sm text-ink2">
                Welcome aboard — your account is active. <Link to="/login">Sign in</Link>
              </p>
            ) : (
              <p className="m-0 text-sm text-ink2">You’re approved. Check your email for the activation link to set your password — it works for 72 hours. If it has expired, ask the floor admin for a new one.</p>
            ))}
          {view.status === 'REJECTED' && (
            <>
              <p className="m-0 text-sm text-ink2">We’re not able to offer you a place at the moment.</p>
              {view.message && <p className="m-0 rounded-lg bg-raised px-3.5 py-3 text-sm whitespace-pre-wrap text-ink2">{view.message}</p>}
            </>
          )}
          <p className="m-0 text-xs text-muted">Keep this link private — anyone with it can see your application.</p>
        </Card>
      )}
    </PublicFrame>
  );
}
